import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import {
  convertDiagnosesFlatAnswersToFormData,
  convertOtherDiagnosesToFlatAnswers,
  convertPrimaryDiagnosisToFlatAnswers
} from 'components/Assessments/helpers.uncontrolled'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { BackgroundFormField } from './formField'

/**
 * Background subsection adapter
 * Handles diagnoses, equipment, allergies, and other background medical info (12 fields)
 */
export class BackgroundAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract diagnosis data using existing helper
    const { primaryDiagnosis, otherDiagnoses } = convertDiagnosesFlatAnswersToFormData(
      getAnswerValuesFromItems(
        flatAnswersArray,
        MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS
      ),
      getAnswerValuesFromItems(
        flatAnswersArray,
        MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS
      )
    )

    // Direct field extractions
    const backgroundEquipment = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT
    )

    const backgroundEquipmentOther = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER
    )?.at(0)

    const backgroundDmeProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER
    )?.at(0)

    const backgroundAllergies = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES
    )

    const backgroundAllergiesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES
    )?.at(0)

    const backgroundWaivers = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS
    )

    const backgroundAdditionalServices = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES
    )

    const backgroundPrognosis = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS
    )?.at(0)

    const backgroundDnr = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR
    )?.at(0)

    const backgroundAdvanceDirectives = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES
    )?.at(0)

    const backgroundAdvanceDirectivesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES
    )?.at(0)

    return {
      [BackgroundFormField.BACKGROUND_PRIMARY_DIAGNOSIS]: primaryDiagnosis,
      [BackgroundFormField.BACKGROUND_OTHER_DIAGNOSIS]: otherDiagnoses,
      [BackgroundFormField.BACKGROUND_EQUIPMENT]: backgroundEquipment,
      [BackgroundFormField.BACKGROUND_EQUIPMENT_OTHER]: backgroundEquipmentOther,
      [BackgroundFormField.BACKGROUND_DME_PROVIDER]: backgroundDmeProvider,
      [BackgroundFormField.BACKGROUND_ALLERGIES]: backgroundAllergies,
      [BackgroundFormField.BACKGROUND_ALLERGIES_NOTES]: backgroundAllergiesNotes,
      [BackgroundFormField.BACKGROUND_WAIVERS]: backgroundWaivers,
      [BackgroundFormField.BACKGROUND_ADDITIONAL_SERVICES]: backgroundAdditionalServices,
      [BackgroundFormField.BACKGROUND_PROGNOSIS]: backgroundPrognosis,
      [BackgroundFormField.BACKGROUND_DNR]: backgroundDnr,
      [BackgroundFormField.BACKGROUND_ADVANCE_DIRECTIVES]: backgroundAdvanceDirectives,
      [BackgroundFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]: backgroundAdvanceDirectivesNotes
    }
  }

  buildFlatAnswers(): Array<{ linkId: string; value: unknown }> {
    const mutations: FlatAnswerMutation[] = []
    if (this.formDataFieldExists(BackgroundFormField.BACKGROUND_PRIMARY_DIAGNOSIS)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS,
        value: convertPrimaryDiagnosisToFlatAnswers(
          this.getFormDataField(BackgroundFormField.BACKGROUND_PRIMARY_DIAGNOSIS)
        )
      })
    }
    if (this.formDataFieldExists(BackgroundFormField.BACKGROUND_OTHER_DIAGNOSIS)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS,
        value: convertOtherDiagnosesToFlatAnswers(
          this.getFormDataField(BackgroundFormField.BACKGROUND_OTHER_DIAGNOSIS) ?? []
        )
      })
    }
    if (this.formDataFieldExists(BackgroundFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES,
        value: this.getFormDataField(BackgroundFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)
      })
    }

    if (this.formDataFieldExists(BackgroundFormField.BACKGROUND_EQUIPMENT_OTHER)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER,
        value: this.getFormDataField(BackgroundFormField.BACKGROUND_EQUIPMENT_OTHER)
      })
    }

    mutations.push(
      ...[
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_EQUIPMENT)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_DME_PROVIDER)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_ALLERGIES)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_ALLERGIES_NOTES)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_WAIVERS)
        },
        {
          linkId:
            MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_ADDITIONAL_SERVICES)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_PROGNOSIS)
        },
        {
          linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_DNR)
        },
        {
          linkId:
            MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES,
          value: this.getFormDataField(BackgroundFormField.BACKGROUND_ADVANCE_DIRECTIVES)
        }
      ]
    )
    return mutations
  }
}
